import {
  AsyncState,
  Fun,
  Updater,
  loadedAsyncState,
  unloadedAsyncState,
} from 'widgets-for-react'
import { ImageData } from '../../image/imageState'
import { SponsorData } from '../../sponsor/sponsorState'
import {
  ComponentSaverState,
  componentSaverStateUpdaters,
} from '../componentSaver/componentSaverState'
import { ComponentTeasersState } from '../componentTeasers/componentTeasersState'
import { loadComponentDataState } from './componentDetailApi'

export type ComponentDetailState = {
  componentStateState: AsyncState<ComponentState>
  showToast: boolean
  amountConverterState: AmountConverterState
}

export type AmountConverterState = {
  multiplier: number | undefined // Multiplier may be undefined if the input text is empty (or otherwise not a number)
  inputText: string
}

export type ComponentState = {
  componentData: ComponentData
  parentComponentNameState: AsyncState<string>
  comparableComponentsState: ComponentTeasersState
  lastViewedComponentUpdateState: AsyncState<{}>
  componentSaverState: ComponentSaverState
  showLoggedOutEditComponentNotification: boolean
}

export type ComponentData = {
  ImageURL: ImageData
  Public: boolean
  Brand: SponsorData
  SubscriptionTypes: { Id: number }[]
  Introduction: string
  Preparation: string
  ServingSuggestions: string
  Name: string
  Id: number
  Draft: boolean
  UserId: string
  ParentComponentId: number
  allergens: AllergensData
  mainIngredients: { id: number; translation: string }[]
  ingredients: {
    id: number
    amount: string
    prepreparations: { id: number; translation: string }[]
    ingredient: { id: number; translation: string }
    unitOfMeasurement: { id: number; translation: string }
  }[]
  techniques: { id: number; translation: string }[]
  appearances: { id: number; translation: string }[]
  textures: { id: number; translation: string }[]
  worldFlavours: { id: number; translation: string }[]
}

export type AllergensData = AllergenData[]
export type AllergenData = {
  title: string
  translation: string
}

export const initialAmountConverterState = (multiplier: number) : AmountConverterState => ({
  multiplier: multiplier,
  inputText: multiplier.toString()
})

export const initialComponentDetailState = (
  componentId: number,
  showToast: boolean
): ComponentDetailState => ({
  showToast: showToast,
  amountConverterState: initialAmountConverterState(1),
  componentStateState: Number.isNaN(componentId)
    ? unloadedAsyncState<ComponentState>()
    : loadComponentDataState(componentId),
})

export const componentDetailStateUpdaters = {
  componentStateStateUpdater:
    (updater: Updater<AsyncState<ComponentState>>) =>
    (state: ComponentDetailState) => ({
      ...state,
      componentStateState: updater(state.componentStateState),
    }),
  componentStateUpdater:
    (
      updater: Fun<
        ComponentState,
        ComponentState & { reloadCollections?: boolean }
      >
    ) =>
    (
      state: ComponentDetailState
    ): ComponentDetailState & { reloadCollections?: boolean } => {
      if (state.componentStateState.kind === 'loaded') {
        const { reloadCollections, ...componentDataViewState } = updater(
          state.componentStateState.value
        )
        return {
          ...state,
          componentStateState: loadedAsyncState(componentDataViewState),
          reloadCollections: reloadCollections,
        }
      }
      return state
    },
  amountConverterStateUpdater:
    (multiplier: number | undefined, text: string) =>
      (state: ComponentDetailState) => (
        {
          ...state,
          amountConverterState: {
            ...state.amountConverterState,
            multiplier: multiplier,
            inputText: text
          },
        }
      ),
  parentComponentNameStateUpdater:
    (updater: Updater<AsyncState<string>>) =>
    (state: ComponentState): ComponentState => ({
      ...state,
      parentComponentNameState: updater(state.parentComponentNameState),
    }),
  lastViewedComponentUpdateStateUpdater:
    (updater: Updater<AsyncState<{}>>) =>
    (state: ComponentState): ComponentState => ({
      ...state,
      lastViewedComponentUpdateState: updater(
        state.lastViewedComponentUpdateState
      ),
    }),
  showToastUpdater:
    (show: boolean) =>
    (state: ComponentDetailState): ComponentDetailState => ({
      ...state,
      showToast: show,
    }),
  comparableComponentsStateUpdater:
    (
      update: Fun<
        ComponentTeasersState,
        ComponentTeasersState & { saveComponentId: number | 'none' }
      >
    ) =>
    (state: ComponentState): ComponentState => {
      const { saveComponentId, ...componentTeaserState } = update(
        state.comparableComponentsState
      )
      return {
        ...state,
        comparableComponentsState: componentTeaserState,
        componentSaverState:
          saveComponentId == 'none'
            ? state.componentSaverState
            : componentSaverStateUpdaters.startSavingUpdater(saveComponentId)(
                state.componentSaverState
              ),
      }
    },
  startSavingUpdater:
    (componentId: number) =>
    (state: ComponentState): ComponentState => ({
      ...state,
      componentSaverState: componentSaverStateUpdaters.startSavingUpdater(
        componentId
      )(state.componentSaverState),
    }),
  showLoggedOutEditComponentNotificationUpdater:
    (showNotification: boolean) =>
    (state: ComponentState): ComponentState => ({
      ...state,
      showLoggedOutEditComponentNotification: showNotification,
    }),
  componentSaverStateUpdater:
    (
      update: Fun<
        ComponentSaverState,
        ComponentSaverState & { reloadCollections?: boolean }
      >
    ) =>
    (
      state: ComponentState
    ): ComponentState & { reloadCollections?: boolean } => {
      const { reloadCollections, ...componentSaverState } = update(
        state.componentSaverState
      )
      return {
        ...state,
        componentSaverState: componentSaverState,
        reloadCollections: reloadCollections,
      }
    },
}
